/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import { connect } from 'react-redux';
import { Query } from '@apollo/client/react/components';
import { Grid } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import {
    object, string, func,
    bool,
} from 'prop-types';
import Media from 'react-media';
import mbpLogger from '@module-logger/mbp';
import { GRAPHQL_ENV } from '../../../../gql';
import SimpleProduct from '../../GraphqlCategoryPage/Partials/GraphqlProductContainer/Partials/GraphqlSimpleProduct/GraphqlSimpleProduct';
import { getUserSubmittedProductFilterZipcode, getLocationType } from '../../../../../state/ducks/App/App-Selectors';
import * as app from '../../../../../state/ducks/App';
import findCategoryById from '../../../../gql/queries/findCategoryById';
import useExperimentServiceAttributes from '../../../../helpers/experimentService/useExperimentServiceAttributes';
import { VertexAIImpressionTracker } from '../../../../helpers/hooks/useVertexAIProductImpression';
import noop from '../../../../helpers/noop';

const brandSelectors = app.ducks.brand.selectors;

const styles = () => ({
    simpleProduct: {
        flex: '0 0 33.333333%',
        marginBottom: '30px',
        maxWidth: '33.333333%',
        position: 'relative',
        width: '100%',
        paddingRight: '10px',
        paddingLeft: '10px',
    },
    productRow: {
        boxSizing: 'border-box',
        marginRight: '-10px',
        marginLeft: '-10px',
        width: 'auto',
    },
    simpleProduct4row: {
        flex: '0 0 25%',
        marginBottom: '30px',
        maxWidth: '25%',
        position: 'relative',
        width: '100%',
        paddingRight: '10px',
        paddingLeft: '10px',
    },
    simpleProduct3row: {
        margin: '0 5px 10px',
        maxWidth: 'calc(33% - 10px)',
        position: 'relative',
        width: '100%',
        paddingRight: '10px',
        paddingLeft: '10px',
    },
});

const ProductListingAdCategoryContainer = ({
    brand, classes, categoryId, trackEvent, trackClickThrough, PLARecommendations,
}) => {
    const { targeting, context, isGraphqlTargetingEnabled } = useExperimentServiceAttributes({ queryName: 'findCategoryById' });
    const CATEGORY_PRODUCT_QUERY = findCategoryById(isGraphqlTargetingEnabled, context?.api === 'experiments');

    return (
        <Query
            query={CATEGORY_PRODUCT_QUERY}
            variables={{
                brand: brand['domain-name'],
                environment: GRAPHQL_ENV,
                locale: 'en-us',
                id: categoryId,
                productOptions: {
                    pageSize: 10,
                    page: 1,
                },
                ...(isGraphqlTargetingEnabled ? { targeting } : {}),
            }}
            context={context}
        >
            {({ loading, error, data }) => {
                if (loading) {
                    return null;
                }

                if (error) {
                    mbpLogger.logError({
                        appName: process.env.npm_package_name,
                        query: CATEGORY_PRODUCT_QUERY,
                        component: 'ProductListingAdCategoryContainer.js',
                        message: 'Error loading data from Graphql',
                        env: GRAPHQL_ENV,
                        error,
                    });
                    return null;
                }

                if (!data) {
                    mbpLogger.logWarning({
                        appName: process.env.npm_package_name,
                        query: CATEGORY_PRODUCT_QUERY,
                        component: 'ProductListingAdCategoryContainer.js',
                        message: 'No data returned for query',
                        env: GRAPHQL_ENV,
                    });
                    return null;
                }

                let productData = [];
                if (data && data.findCategoryById !== null && data.findCategoryById.products !== null) {
                    productData = data.findCategoryById.products;
                }

                return (
                    <>
                        <VertexAIImpressionTracker
                            products={productData}
                            experiments={data?.findCategoryById?.experiments}
                            attributionToken={data?.findCategoryById?.attributionToken}
                            pageType="category"
                        />
                        <Media
                            query="(max-width: 600px)"
                        >
                            {(matches) => (matches ? (
                                <Grid container className={classes.productRow}>
                                    {productData.length !== 0
                                        ? productData.map((product) => {
                                            const {
                                                name, image, seo: { url }, skuPriceRange, availability, partNumber, brandId,
                                            } = product;
                                            return (
                                                <Grid item key={product.name} className={classes.simpleProduct3row}>
                                                    {/* for the product not available flow we need to know whether the simple product component was rendered on a category page vs the homepage or any other location */}
                                                    {/* to do this we check whether the product container got categoryData as opposed to some other kind of graphql data */}
                                                    <SimpleProduct
                                                        name={name}
                                                        image={image}
                                                        url={url}
                                                        skuPriceRange={skuPriceRange}
                                                        availability={availability}
                                                        fromCategoryPage={typeof categoryData === 'object'}
                                                        categoryId={categoryId}
                                                        partNumber={partNumber}
                                                        trackEvent={trackEvent}
                                                        showSimpleProductRedesignAbTest={false}
                                                        productBrand={brandId}
                                                        productClickCallback={() => trackClickThrough('', product)} // trackClickThrough from useClickStreamCustomEventsTracking hook
                                                        PLARecommendations={PLARecommendations}
                                                    />
                                                </Grid>
                                            );
                                        })
                                        : null}
                                </Grid>
                            ) : (
                                <Grid container className={classes.productRow}>
                                    {productData.length !== 0
                                        ? productData.map((product) => {
                                            const {
                                                name, image, seo: { url }, skuPriceRange, availability, partNumber, brandId,
                                            } = product;
                                            return (
                                                <Grid item key={product.name} className={classes.simpleProduct4row}>
                                                    {/* for the product not available flow we need to know whether the simple product component was rendered on a category page vs the homepage or any other location */}
                                                    {/* to do this we check whether the product container got categoryData as opposed to some other kind of graphql data */}
                                                    <SimpleProduct
                                                        name={name}
                                                        image={image}
                                                        url={url}
                                                        skuPriceRange={skuPriceRange}
                                                        availability={availability}
                                                        fromCategoryPage={typeof categoryData === 'object'}
                                                        categoryId={categoryId}
                                                        partNumber={partNumber}
                                                        trackEvent={trackEvent}
                                                        showSimpleProductRedesignAbTest={false}
                                                        productBrand={brandId}
                                                        productClickCallback={() => trackClickThrough('', product)}
                                                        PLARecommendations={PLARecommendations}
                                                    />
                                                </Grid>
                                            );
                                        })
                                        : null}
                                </Grid>
                            ))}
                        </Media>
                    </>
                );
            }}
        </Query>
    );
};

ProductListingAdCategoryContainer.propTypes = {
    classes: object.isRequired,
    categoryId: string.isRequired,
    brand: object.isRequired,
    trackEvent: func.isRequired,
    trackClickThrough: func,
    PLARecommendations: bool,
};

ProductListingAdCategoryContainer.defaultProps = {
    trackClickThrough: noop,
    PLARecommendations: false,
};

const mapStateToProps = (state) => ({
    brand: brandSelectors.getBrand(state),
    locationType: getLocationType(state),
    userSubmittedZip: getUserSubmittedProductFilterZipcode(state),
});

export default connect(mapStateToProps)(withStyles(styles)(ProductListingAdCategoryContainer));
